import { resolveDestination } from "routing/to";
import { Menu, useTheme } from "@mui/material";
import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
  NavigationBarButtonStyles,
  makePaperStyles,
  MenuItem
} from "../NavigationBarButton.styles";
import { useAuthState } from "../../../../hooks";
import { NavigationButtonWithSubroutesProps } from "../../../../types";
import { withFeatureVisibility } from "../../../../HoC";

export const NavigationBarButtonWithSubroutes = ({
  translationKey,
  subroutes,
  onClick,
  shopId,
  requiredPermission
}: NavigationButtonWithSubroutesProps) => {
  const theme = useTheme();

  const { pathname } = useLocation();

  const { t } = useTranslation();

  const { checkPermission } = useAuthState();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (menuAnchor) {
        setMenuAnchor(null);
      } else {
        setMenuAnchor(event.currentTarget);
      }
    },
    [menuAnchor]
  );

  const handleCloseNavMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleSubrouteClick = useCallback(() => {
    onClick();
    setMenuAnchor(null);
  }, [onClick]);

  const parsedSubroutes = useMemo(
    () =>
      subroutes.map(({ translationKey, path, requiredPermission, featureFlag }) => {
        if (requiredPermission && !checkPermission(requiredPermission)) return null;

        const active = pathname === resolveDestination(path.path, shopId);
        const to = resolveDestination(path.path, shopId);
        if (to === undefined) return null;

        if (featureFlag) {
          const Component = withFeatureVisibility(MenuItem, featureFlag);

          return (
            <Component disabled={active} key={translationKey} onClick={handleSubrouteClick} to={to}>
              {t(translationKey)}
            </Component>
          );
        }

        return (
          <MenuItem disabled={active} key={translationKey} onClick={handleSubrouteClick} to={to}>
            {t(translationKey)}
          </MenuItem>
        );
      }),
    [checkPermission, handleSubrouteClick, pathname, shopId, subroutes, t]
  );

  const routeActive = subroutes.some(
    ({ path }) => resolveDestination(path.path, shopId) === pathname
  );
  const active = Boolean(menuAnchor);

  if (
    !parsedSubroutes.some(Boolean) ||
    (requiredPermission && !checkPermission(requiredPermission))
  ) {
    return null;
  }

  return (
    <React.Fragment>
      <NavigationBarButtonStyles
        withSubroutes
        selected={routeActive}
        active={active}
        onClick={handleOpenNavMenu}
      >
        {t(translationKey)}
      </NavigationBarButtonStyles>
      <Menu
        id={`${translationKey}-menu`}
        anchorEl={menuAnchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        open={active}
        slotProps={{
          paper: {
            sx: makePaperStyles(theme)
          }
        }}
        onClose={handleCloseNavMenu}
      >
        {parsedSubroutes}
      </Menu>
    </React.Fragment>
  );
};
